// Onboarding business-verification + consent capture (B2B legal brief
// 2026-06-11). This is the legal evidence that EcoIntel sells business-to-
// business only: every grant logged from these definitions carries the three
// things our solicitor requires — WORDING VERSION + TIMESTAMP + USER.
//
// IMPORTANT — versioning is the legal anchor:
//   - Each consent has a `version`. If you change the `label`/wording, you
//     MUST bump `version` here AND append a matching versions[] entry in
//     `scripts/seed-consent-types.mjs`, so historic grants stay attributable
//     to the exact text the user agreed to.
//   - Records are APPEND-ONLY on users/{uid}.consents[] — never edit or remove
//     an existing record. The "effective" state for a consent_id is the most
//     recent record by granted_at (mirrors the backend effectiveConsentMap in
//     backend/analytics-server/marcusIngestion.js).

import { LEGAL_LINKS } from './legalLinks';

// Stable machine identifier for the free-text "Other" business type.
export const BUSINESS_TYPE_OTHER = 'other';

// Definitive UK + EU B2B business-type list from the brief. No consumer
// option by design (s2(4) Consumer Rights Act — the burden is on us to show
// the buyer acted for business purposes). `value` strings are stored on
// users/{uid}.businessType and must stay stable.
export const BUSINESS_TYPES = Object.freeze([
  { value: 'sole_trader', label: 'Sole trader / self-employed' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'llp', label: 'Limited liability partnership (LLP)' },
  {
    value: 'limited_company',
    label: 'Limited company / corporation (e.g. Ltd, PLC, GmbH, SARL, SAS, S.r.l., B.V., Sp. z o.o.)',
  },
  { value: 'charity_nonprofit', label: 'Charity, non-profit or community interest company' },
  { value: 'trust_estate', label: 'Trust or estate' },
  { value: 'agri_coop', label: 'Agricultural cooperative or producer organisation' },
  { value: 'public_body', label: 'Public body / government / local authority' },
  { value: BUSINESS_TYPE_OTHER, label: 'Other (please specify)' },
]);

const BUSINESS_TYPE_VALUES = new Set(BUSINESS_TYPES.map((t) => t.value));

export function isValidBusinessType(value) {
  return BUSINESS_TYPE_VALUES.has(value);
}

// Consent definitions captured at onboarding (CompleteProfile).
export const ONBOARDING_CONSENTS = Object.freeze({
  business_purpose: {
    id: 'business_purpose_declaration',
    version: '1.1',
    required: true,
    label:
      'I confirm I am creating this EcoIntel account for business and/or professional purposes, and not as a consumer.',
  },
  terms_of_service: {
    id: 'terms_of_service',
    version: '1.0',
    required: true,
  },
  privacy_policy: {
    id: 'privacy_policy',
    version: '1.0',
    required: true,
  },
  // v1.1 (handoff 11): split into a short display label + a full description so
  // it renders uniformly in the Profile Consents card. Removing the em dash from
  // the old single-sentence label is a wording change, so the version is bumped
  // and a matching v1.1 entry is seeded in scripts/seed-consent-types.mjs.
  marketing_emails: {
    id: 'marketing_emails',
    version: '1.1',
    required: false,
    label: 'Product updates & newsletters',
    description:
      'Occasional product updates, new feature announcements and newsletters. Optional, separate from account, billing and report emails, and you can unsubscribe at any time.',
  },
});

// The ToS + Privacy click-wrap is a SINGLE checkbox (brief §3) but is logged
// as TWO consent records so each document is independently auditable to its
// own version. This is the exact wording shown next to that single checkbox.
export const TERMS_PRIVACY_LABEL =
  'I agree to the Terms of Service and have read the Privacy Policy.';

export const TERMS_PRIVACY_CONSENTS = Object.freeze([
  ONBOARDING_CONSENTS.terms_of_service,
  ONBOARDING_CONSENTS.privacy_policy,
]);

/**
 * Build an append-only consent record carrying the legal evidence triplet:
 * wording version + timestamp + user. Also snapshots the exact wording shown
 * (self-describing if the text later changes) and the granted boolean.
 *
 * @param {{id:string, version:string, label?:string}} def Consent definition.
 * @param {object} opts
 * @param {string} opts.uid           Granting user's uid.
 * @param {boolean} opts.granted      Whether consent was given.
 * @param {string} [opts.wording]     Exact wording shown (defaults to def.label).
 * @param {string} [opts.nowIso]      ISO timestamp (shared across a batch).
 * @param {string} [opts.source]      Where the grant was captured.
 */
export function buildConsentRecord(def, { uid, granted, wording, nowIso, source } = {}) {
  return {
    consent_id: def.id,
    version: def.version,
    granted: granted === true,
    granted_at: nowIso || new Date().toISOString(),
    uid: uid || null,
    wording: typeof wording === 'string' ? wording : def.label || null,
    source: source || 'onboarding_complete_profile',
  };
}

// ---------------------------------------------------------------------------
// Account-level permissions surfaced + managed in the Profile (handoff 11).
// Same append-only model as ONBOARDING_CONSENTS: every change writes a new
// buildConsentRecord to users/{uid}.consents[]. Optional + revocable.
// Two of these gate disclosure behaviour server-side (see handoff 10 §5):
//   support_access       -> support may read Lift evidence (logged); else denied
//   ai_assist_disclosure -> the disclosure AI may process uploaded docs; else off
//   platform_improvement -> anonymised data may be retained for product dev;
//                           the identifiable original is purged on account close
// Default is OFF for all three (absence of a record = not granted); server-side
// enforcement is a separate, tracked task and stays default-off until it lands.
// ---------------------------------------------------------------------------
export const ACCOUNT_CONSENTS = Object.freeze({
  support_access: {
    id: 'support_access',
    version: '1.0',
    required: false,
    label: 'EcoIntel support access',
    description:
      'Let our support team open your account and data to help when you ask. ' +
      'Off by default. Every access is logged, and you can switch it off again at any time.',
  },
  platform_improvement: {
    id: 'platform_improvement',
    version: '1.0',
    required: false,
    label: 'Help improve the platform',
    description:
      'Allow EcoIntel to use your data in anonymised form to make the service better, ' +
      'including after you close your account. It never identifies you or your land.',
  },
  ai_assist_disclosure: {
    id: 'ai_assist_disclosure',
    version: '1.0',
    required: false,
    label: 'AI assistance for disclosure',
    description:
      "Let EcoIntel's AI help draft and structure your disclosure evidence. " +
      'You always attest the final wording yourself, and AI never counts as evidence.',
  },
});

// The agreements we show read-only in the Profile (captured at sign-up).
// `verb` is how the row labels the grant date. Links point at the canonical
// external legal pages (LEGAL_LINKS), never invented in-app routes.
export const PROFILE_AGREEMENTS = Object.freeze([
  { def: ONBOARDING_CONSENTS.business_purpose, title: 'Business-purpose declaration',
    note: 'Account created for business and/or professional purposes, not as a consumer.', verb: 'Agreed', link: null },
  { def: ONBOARDING_CONSENTS.terms_of_service, title: 'Terms of Service',
    note: null, verb: 'Agreed', link: LEGAL_LINKS.terms },
  { def: ONBOARDING_CONSENTS.privacy_policy, title: 'Privacy Policy',
    note: null, verb: 'Read', link: LEGAL_LINKS.privacy },
]);

// The optional permissions, in display order. marketing_emails is the existing
// one (moved here from Profile Settings); the other three are new.
export const PROFILE_PERMISSIONS = Object.freeze([
  ONBOARDING_CONSENTS.marketing_emails,
  ACCOUNT_CONSENTS.support_access,
  ACCOUNT_CONSENTS.platform_improvement,
  ACCOUNT_CONSENTS.ai_assist_disclosure,
]);

// Top-level mirror field per consent_id, written alongside the append-only
// record so Firestore rules / server queries read current state in O(1)
// without scanning consents[]. The array stays the source of legal truth.
export const CONSENT_MIRROR_FIELD = Object.freeze({
  marketing_emails: 'marketingOptIn',          // already used elsewhere - keep
  support_access: 'supportAccessOptIn',
  platform_improvement: 'platformImprovementOptIn',
  ai_assist_disclosure: 'aiAssistDisclosureOptIn',
});

/**
 * Effective ("current") state of a consent_id from the append-only array:
 * most-recent record by granted_at wins. Mirrors the backend effectiveConsentMap.
 * @returns {object|null} the latest record, or null if never recorded.
 */
export function effectiveConsent(consents, consentId) {
  if (!Array.isArray(consents)) return null;
  let latest = null;
  for (const rec of consents) {
    if (!rec || rec.consent_id !== consentId) continue;
    if (!latest || (rec.granted_at || '') > (latest.granted_at || '')) latest = rec;
  }
  return latest;
}

/** Boolean convenience: is this consent currently granted? */
export function isConsentGranted(consents, consentId) {
  const rec = effectiveConsent(consents, consentId);
  return rec ? rec.granted === true : false;
}
